import { cn } from '@/lib/utils'
import { ChevronRightIcon } from 'lucide-react'
import { type KeyboardEvent, type ReactNode, useRef, useState } from 'react'

interface INodeVisivel {
    id: string
    nivel: number
    temFilhos: boolean
    expandido: boolean
}

interface IProps<T> {
    items: T[]
    getId: (item: T) => string
    /** Filhos do nó. Padrão: propriedade `children` do item. */
    getChildren?: (item: T) => T[] | undefined
    renderLabel: (
        item: T,
        meta: { nivel: number; expandido: boolean },
    ) => ReactNode
    /** Quando definido, a expansão é controlada pelo consumidor. */
    expandedIds?: string[]
    onExpandedChange?: (ids: string[]) => void
    defaultExpandedIds?: string[]
    className?: string
}

/**
 * Árvore hierárquica acessível. A label é responsabilidade do consumidor
 * (`renderLabel`), incluindo checkboxes e botões de ação.
 */
export default function Tree<T>({
    items,
    getId,
    getChildren = item => (item as { children?: T[] }).children,
    renderLabel,
    expandedIds,
    onExpandedChange,
    defaultExpandedIds,
    className,
}: IProps<T>) {
    const [expandidosInternos, setExpandidosInternos] = useState(
        () => defaultExpandedIds ?? [],
    )
    const [focoId, setFocoId] = useState<string>()
    const raizRef = useRef<HTMLUListElement>(null)

    const expandidos = new Set(expandedIds ?? expandidosInternos)

    function alternar(id: string) {
        const proximos = expandidos.has(id)
            ? [...expandidos].filter(it => it !== id)
            : [...expandidos, id]

        if (!expandedIds) setExpandidosInternos(proximos)
        onExpandedChange?.(proximos)
    }

    function achatarVisiveis(
        lista: T[],
        nivel: number,
        acumulado: INodeVisivel[] = [],
    ): INodeVisivel[] {
        for (const item of lista) {
            const id = getId(item)
            const filhos = getChildren(item) ?? []
            const expandido = expandidos.has(id)

            acumulado.push({
                id,
                nivel,
                temFilhos: !!filhos.length,
                expandido,
            })

            if (filhos.length && expandido)
                achatarVisiveis(filhos, nivel + 1, acumulado)
        }

        return acumulado
    }

    const visiveis = achatarVisiveis(items, 0)
    const focavelId = visiveis.some(it => it.id === focoId)
        ? focoId
        : visiveis[0]?.id

    function focar(id?: string) {
        if (!id) return
        raizRef.current
            ?.querySelector<HTMLElement>(`[data-tree-id="${CSS.escape(id)}"]`)
            ?.focus()
    }

    function aoTeclar(evento: KeyboardEvent, node: INodeVisivel) {
        const indice = visiveis.findIndex(it => it.id === node.id)

        switch (evento.key) {
            case 'ArrowDown':
                focar(visiveis[indice + 1]?.id)
                break
            case 'ArrowUp':
                focar(visiveis[indice - 1]?.id)
                break
            case 'ArrowRight':
                if (!node.temFilhos) return
                if (node.expandido) focar(visiveis[indice + 1]?.id)
                else alternar(node.id)
                break
            case 'ArrowLeft':
                if (node.temFilhos && node.expandido) alternar(node.id)
                else
                    focar(
                        visiveis
                            .slice(0, indice)
                            .reverse()
                            .find(it => it.nivel < node.nivel)?.id,
                    )
                break
            case 'Enter':
            case ' ':
                if (node.temFilhos) alternar(node.id)
                break
            default:
                return
        }

        evento.preventDefault()
        evento.stopPropagation()
    }

    function renderizarNivel(lista: T[], nivel: number) {
        return lista.map(item => {
            const id = getId(item)
            const filhos = getChildren(item) ?? []
            const temFilhos = !!filhos.length
            const expandido = expandidos.has(id)
            const node = { id, nivel, temFilhos, expandido }

            return (
                <li
                    key={id}
                    role="treeitem"
                    aria-expanded={temFilhos ? expandido : undefined}
                    aria-level={nivel + 1}
                    data-tree-id={id}
                    tabIndex={focavelId === id ? 0 : -1}
                    className="group/item outline-none"
                    onFocus={() => setFocoId(id)}
                    onKeyDown={evento => aoTeclar(evento, node)}
                    onClick={evento => {
                        evento.stopPropagation()
                        if (temFilhos) alternar(id)
                    }}
                >
                    <div
                        style={{ paddingLeft: `${nivel * 1.5 + 0.5}rem` }}
                        className={cn(
                            'flex items-center gap-1 rounded-sm py-1 pr-2',
                            'cursor-pointer select-none hover:bg-accent transition-colors duration-50',
                            'group-focus-visible/item:ring-2 group-focus-visible/item:ring-ring',
                        )}
                    >
                        <ChevronRightIcon
                            aria-hidden
                            className={cn(
                                'size-4 shrink-0 text-muted-foreground',
                                'transition-transform duration-200',
                                expandido && 'rotate-90',
                                !temFilhos && 'invisible',
                            )}
                        />
                        <div className="min-w-0 flex-1">
                            {renderLabel(item, { nivel, expandido })}
                        </div>
                    </div>
                    {temFilhos && expandido && (
                        <ul role="group">
                            {renderizarNivel(filhos, nivel + 1)}
                        </ul>
                    )}
                </li>
            )
        })
    }

    return (
        <ul ref={raizRef} role="tree" className={className}>
            {renderizarNivel(items, 0)}
        </ul>
    )
}
